'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Separator,
  cn,
} from '@baseball-near-you/ui';
import { PATHS } from '@bb/lib/constants';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { Loader2, Menu, User as UserIcon } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { LoginModal } from './Auth/LoginModal/LoginModal';
import { tabs } from './Navbar';
import { PageTab } from './PageTab';
import { SignOutDropdownMenuItem } from './SignOutDropdownMenuItem';

export function ProfileButton() {
  const { userProfile: user, userProfileLoading: userLoading } =
    useUserProfile();

  return (
    <div className="flex">
      {!!user || userLoading ? (
        <ProfileButtonDropdownMenu />
      ) : (
        <>
          <div className="hidden space-x-2 sm:inline">
            <SignUpButton />
            <LoginModal />
          </div>
          <div className="flex h-10 items-center space-x-3 sm:hidden">
            <SignUpButton />
            <ProfileButtonDropdownMenu menuTrigger={<Menu />} />
          </div>
        </>
      )}
    </div>
  );
}

export function SignUpButton() {
  const { userProfile: user, userProfileLoading: userLoading } =
    useUserProfile();
  return !user && !userLoading ? (
    <Link href={PATHS.Onboarding}>
      <Button className="whitespace-nowrap bg-red">Sign up</Button>
    </Link>
  ) : null;
}

export function ProfileButtonDropdownMenu(props: {
  menuTrigger?: React.ReactNode;
}) {
  const { userProfile: user, userProfileLoading: userLoading } =
    useUserProfile();
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        className={cn(
          'flex h-full items-center justify-center outline-hidden ring-0 transition-colors',
          !user && !userLoading && 'rounded-md border border-gray-400',
          open && !user && !userLoading && 'border-gray-700 bg-gray-200'
        )}
      >
        {props.menuTrigger ?? (
          <div className="flex items-center justify-between gap-x-2 rounded-sm border border-gray-300 px-3 py-2 shadow-none transition-all hover:shadow-sm">
            <ProfileButtonAvatar />
            <Menu size={20} className="text-gray-700" />
          </div>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="flex w-screen flex-col justify-around md:w-min"
      >
        <div className="contents sm:hidden">
          {tabs.map((tab) => (
            <DropdownMenuItem
              key={tab.path}
              className="title py-3 text-lg md:py-0.5 md:text-lg"
            >
              <PageTab label={tab.label} path={tab.path} key={tab.label} />
            </DropdownMenuItem>
          ))}
        </div>
        <Separator className="sm:hidden" />
        {user ? (
          <>
            <Link href={PATHS.Settings}>
              <DropdownMenuItem className="title py-3 text-lg md:py-0.5 md:text-lg">
                SETTINGS
              </DropdownMenuItem>
            </Link>
            <Separator className="hidden sm:inline" />
            <SignOutDropdownMenuItem />
          </>
        ) : (
          <Link href={PATHS.Login}>
            <DropdownMenuItem className="title py-3 text-lg md:py-0.5 md:text-lg">
              LOGIN
            </DropdownMenuItem>
          </Link>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ProfileButtonAvatar() {
  const { userProfile: user, userProfileLoading: userLoading } =
    useUserProfile();

  return (
    <Avatar className="rounded-full border border-gray-300">
      <AvatarImage src={user?.image ?? undefined} />
      <AvatarFallback>
        {userLoading ? (
          <div className="flex h-full w-full items-center justify-center bg-gray-100">
            <Loader2 size={18} className="animate-spin text-gray-400" />
          </div>
        ) : user?.firstName?.[0] && user?.lastName?.[0] ? (
          user?.firstName[0].toUpperCase() + user?.lastName[0].toUpperCase()
        ) : (
          <UserIcon />
        )}
      </AvatarFallback>
    </Avatar>
  );
}
